import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, Lock, Eye, EyeOff, Save,
  ShoppingBag, CalendarCheck,
  ChevronRight, LogOut, Edit3, Check
} from 'lucide-react';
import { api } from '../config/api';

const Profile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({ name: '', phone: '', password: '', confirmPassword: '' });
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user) { navigate('/login'); return; }
    setUserEmail(user.email || '');
    fetchProfile();
    fetchOrders();
    fetchBookings();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/profile.php');
      if (res.data.success) {
        setFormData(prev => ({ ...prev, name: res.data.data.name, phone: res.data.data.phone || '' }));
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await api.get('/my-orders.php');
      if (res.data.success) setOrders(res.data.data || []);
    } catch { /* silent */ }
  };

  const fetchBookings = async () => {
    try {
      const res = await api.get('/bookings.php');
      if (res.data.success) setBookings(res.data.data || []);
    } catch { /* silent */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = { name: formData.name, phone: formData.phone };
      if (formData.password) payload.password = formData.password;
      const res = await api.put('/profile.php', payload);
      if (res.data.success) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.name = formData.name;
        localStorage.setItem('user', JSON.stringify(user));
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
      } else {
        setMessage({ type: 'error', text: res.data.message || 'Update failed.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Server error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const getStatusColor = (status) => {
    const map = {
      confirmed: '#27AE60', pending: '#F39C12', completed: '#3498DB',
      cancelled: '#E74C3C', preparing: '#9B59B6', delivered: '#27AE60'
    };
    return map[status?.toLowerCase()] || '#999';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#faf8f5' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'My Profile', icon: <Edit3 size={16} /> },
    { id: 'orders', label: `Orders (${orders.length})`, icon: <ShoppingBag size={16} /> },
    { id: 'bookings', label: `Bookings (${bookings.length})`, icon: <CalendarCheck size={16} /> },
  ];

  return (
    <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '90px', paddingBottom: '80px', fontFamily: "'Poppins', sans-serif" }}>
      <div className="container">

        {/* Profile Header Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1A1A, #2c2c2c)',
          borderRadius: '20px', padding: '32px 40px', marginBottom: '28px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '20px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {/* Avatar */}
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'rgba(212,175,55,0.15)',
              border: '3px solid rgba(212,175,55,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.8rem', fontWeight: 700, color: '#D4AF37',
              fontFamily: "'Playfair Display', serif"
            }}>
              {formData.name.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h2 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', fontWeight: 600, marginBottom: '4px' }}>
                {formData.name || 'My Account'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '8px' }}>
                <Mail size={12} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                {userEmail}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <span style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37', padding: '3px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>
                  ✦ Valued Customer
                </span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {[
              { label: 'Orders', value: orders.length, icon: <ShoppingBag size={18} color="#D4AF37" /> },
              { label: 'Bookings', value: bookings.length, icon: <CalendarCheck size={18} color="#D4AF37" /> },
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '4px' }}>
                  {stat.icon}
                  <span style={{ color: '#D4AF37', fontSize: '1.5rem', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
                    {stat.value}
                  </span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 0 }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>

          {/* Logout */}
          <button onClick={handleLogout} style={{
            background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.25)',
            color: '#e74c3c', borderRadius: '10px', padding: '10px 20px',
            fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px',
            fontFamily: 'inherit', transition: 'all 0.3s'
          }}>
            <LogOut size={15} /> Logout
          </button>
        </div>

        {/* Quick Actions */}
        <div className="row g-3 mb-4">
          {[
            { label: 'Browse Menu', to: '/menu', icon: '🍽', desc: 'Explore all dishes' },
            { label: 'Reserve Table', to: '/booking', icon: '📅', desc: 'Book for dining' },
            { label: 'View Cart', to: '/cart', icon: '🛒', desc: 'Complete your order' },
          ].map((action, i) => (
            <div key={i} className="col-md-4">
              <Link to={action.to} style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#fff', borderRadius: '14px', padding: '20px',
                  border: '1px solid rgba(212,175,55,0.1)',
                  display: 'flex', alignItems: 'center', gap: '14px',
                  transition: 'all 0.3s', boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.1)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <span style={{ fontSize: '1.8rem' }}>{action.icon}</span>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px', color: '#1A1A1A' }}>{action.label}</p>
                    <p style={{ fontSize: '0.78rem', color: '#999', marginBottom: 0 }}>{action.desc}</p>
                  </div>
                  <ChevronRight size={16} color="#D4AF37" style={{ marginLeft: 'auto' }} />
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div style={{
          backgroundColor: '#fff', borderRadius: '14px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden'
        }}>
          <div style={{ borderBottom: '1px solid #f0f0f0', display: 'flex' }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                flex: 1, padding: '16px', background: 'none',
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: '0.85rem', fontWeight: activeTab === tab.id ? 600 : 400,
                color: activeTab === tab.id ? '#D4AF37' : '#777',
                borderBottom: activeTab === tab.id ? '3px solid #D4AF37' : '3px solid transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s'
              }}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div style={{ padding: '32px' }}>

            {/* ── PROFILE TAB ── */}
            {activeTab === 'profile' && (
              <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
                {message.text && (
                  <div style={{
                    padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
                    textAlign: 'center', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center',
                    backgroundColor: message.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                    color: message.type === 'success' ? '#2E7D32' : '#C62828'
                  }}>
                    {message.type === 'success' && <Check size={16} />}
                    {message.text}
                  </div>
                )}

                {[
                  { label: 'Full Name', field: 'name', type: 'text', icon: <User size={15} />, required: true },
                  { label: 'Phone Number', field: 'phone', type: 'tel', icon: <Phone size={15} />, required: false },
                ].map(({ label, field, type, icon, required }) => (
                  <div className="mb-3" key={field}>
                    <label style={{ fontWeight: 500, fontSize: '0.85rem', color: '#444', marginBottom: '7px', display: 'block' }}>
                      {label}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#D4AF37' }}>{icon}</span>
                      <input type={type} className="form-control" value={formData[field]}
                        onChange={e => setFormData({ ...formData, [field]: e.target.value })}
                        required={required} style={{ paddingLeft: '42px' }} />
                    </div>
                  </div>
                ))}

                <div style={{ height: '1px', background: '#f0f0f0', margin: '24px 0' }} />
                <h6 style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '16px', color: '#444' }}>
                  <Lock size={14} style={{ marginRight: '6px', verticalAlign: 'middle', color: '#D4AF37' }} />
                  Change Password (optional)
                </h6>

                {[
                  { label: 'New Password', field: 'password', placeholder: 'Leave blank to keep current' },
                  { label: 'Confirm Password', field: 'confirmPassword', placeholder: 'Re-enter new password' },
                ].map(({ label, field, placeholder }) => (
                  <div className="mb-3" key={field}>
                    <label style={{ fontWeight: 500, fontSize: '0.85rem', color: '#444', marginBottom: '7px', display: 'block' }}>{label}</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#D4AF37' }}><Lock size={15} /></span>
                      <input type={showPassword ? 'text' : 'password'} className="form-control"
                        value={formData[field]} placeholder={placeholder}
                        onChange={e => setFormData({ ...formData, [field]: e.target.value })}
                        style={{ paddingLeft: '42px', paddingRight: '42px' }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 0 }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                ))}

                <button type="submit" disabled={saving} className="btn btn-gold w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{ padding: '14px', fontSize: '0.9rem', marginTop: '8px' }}>
                  <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {/* ── ORDERS TAB ── */}
            {activeTab === 'orders' && (
              <div>
                {orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
                    <ShoppingBag size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                    <p style={{ fontSize: '1rem', marginBottom: '20px' }}>No orders yet</p>
                    <Link to="/menu" className="btn btn-gold" style={{ padding: '12px 32px', fontSize: '0.85rem' }}>
                      Explore Menu
                    </Link>
                  </div>
                ) : (
                  <div>
                    {orders.map((order, i) => (
                      <div key={i} style={{
                        border: '1px solid #f0f0f0', borderRadius: '12px', padding: '20px',
                        marginBottom: '14px', display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
                        transition: 'all 0.3s'
                      }}>
                        <div>
                          <p style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '4px' }}>
                            Order #{order.order_id}
                          </p>
                          <p style={{ color: '#888', fontSize: '0.82rem', marginBottom: '4px' }}>
                            {order.order_date ? new Date(order.order_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </p>
                          <p style={{ color: '#555', fontSize: '0.85rem', marginBottom: 0 }}>
                            {order.items?.length || 0} item(s) · UGX {Number(order.total_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <span style={{
                          padding: '6px 16px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600,
                          backgroundColor: `${getStatusColor(order.status)}18`,
                          color: getStatusColor(order.status), textTransform: 'capitalize'
                        }}>
                          {order.status || 'pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── BOOKINGS TAB ── */}
            {activeTab === 'bookings' && (
              <div>
                {bookings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
                    <CalendarCheck size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                    <p style={{ fontSize: '1rem', marginBottom: '20px' }}>No reservations yet</p>
                    <Link to="/booking" className="btn btn-gold" style={{ padding: '12px 32px', fontSize: '0.85rem' }}>
                      Reserve a Table
                    </Link>
                  </div>
                ) : (
                  <div>
                    {bookings.map((booking, i) => (
                      <div key={i} style={{
                        border: '1px solid #f0f0f0', borderRadius: '12px', padding: '20px',
                        marginBottom: '14px', display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px'
                      }}>
                        <div>
                          <p style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '4px' }}>
                            📅 {booking.date ? new Date(booking.date).toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                          </p>
                          <p style={{ color: '#777', fontSize: '0.82rem', marginBottom: '4px' }}>
                            ⏰ {booking.time || '—'} · 👥 {booking.guests_count || 1} guest(s)
                          </p>
                          {booking.special_requests && (
                            <p style={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: 0 }}>
                              "{booking.special_requests}"
                            </p>
                          )}
                        </div>
                        <span style={{
                          padding: '6px 16px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600,
                          backgroundColor: `${getStatusColor(booking.status)}18`,
                          color: getStatusColor(booking.status), textTransform: 'capitalize'
                        }}>
                          {booking.status || 'pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Profile;

